/**
 * Fix DW (2026-09-23): "En la parte de empleado debe desplegarse todo el personal
 * en la nómina. Y debe poderse hacer multicheck a varios personales" · "Lo mismo en
 * el parte diario obra" (Sergio, 23/09).
 *
 * Hasta ahora el Parte Diario Administrativo mostraba un desplegable de un solo
 * empleado y además filtraba por `status === "activo"`, así que con toda la nómina
 * en otro estado el desplegable salía vacío. Este selector lista SIEMPRE a todo el
 * personal, marca el estado de cada uno y permite elegir varios a la vez.
 */
import { useState, useMemo, useRef, useEffect } from "react";
import { ChevronDown, Search, X, Check } from "lucide-react";

const ESTADO_CHIP = {
  activo: "bg-emerald-100 text-emerald-700",
  licencia: "bg-amber-100 text-amber-700",
  inactivo: "bg-gray-100 text-gray-500",
};

function norm(s) {
  return (s || "").toString().normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export default function SelectorEmpleados({ workers, value, onChange, testid = "selector-empleados", placeholder = "Seleccionar..." }) {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const caja = useRef(null);
  const seleccionados = value || [];

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);

  const lista = useMemo(() => {
    const todos = (workers || []).map(w => ({
      id: w.id || w._id, name: w.name || "", status: w.status || "", role: w.role_title || "",
    })).filter(w => w.id);
    const q = norm(busqueda.trim());
    const filtrados = q ? todos.filter(w => norm(w.name).includes(q) || norm(w.role).includes(q)) : todos;
    // Los activos primero, después licencia, después inactivos; dentro de cada grupo por nombre.
    const peso = { activo: 0, licencia: 1 };
    return filtrados.sort((a, b) =>
      (peso[a.status] ?? 2) - (peso[b.status] ?? 2) || a.name.localeCompare(b.name, "es"));
  }, [workers, busqueda]);

  const alternar = (id) => {
    onChange(seleccionados.includes(id) ? seleccionados.filter(x => x !== id) : [...seleccionados, id]);
  };
  const todosVisibles = lista.map(w => w.id);
  const todosMarcados = todosVisibles.length > 0 && todosVisibles.every(id => seleccionados.includes(id));
  const alternarTodos = () => {
    onChange(todosMarcados ? seleccionados.filter(id => !todosVisibles.includes(id))
                           : [...new Set([...seleccionados, ...todosVisibles])]);
  };

  const etiqueta = seleccionados.length === 0
    ? placeholder
    : seleccionados.length === 1
      ? ((workers || []).find(w => (w.id || w._id) === seleccionados[0])?.name || "1 empleado")
      : `${seleccionados.length} empleados seleccionados`;

  return (
    <div className="relative" ref={caja} data-testid={testid}>
      <button type="button" onClick={() => setAbierto(a => !a)}
        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white flex items-center justify-between gap-2 hover:border-[#2E6DA4] focus:ring-2 focus:ring-[#2E6DA4]/20"
        data-testid={`${testid}-btn`}>
        <span className={seleccionados.length ? "text-gray-900 truncate" : "text-gray-400"}>{etiqueta}</span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {seleccionados.length > 0 && (
            <span onClick={e => { e.stopPropagation(); onChange([]); }}
              className="text-gray-400 hover:text-red-500" title="Quitar la selección"
              data-testid={`${testid}-limpiar`}><X size={12} /></span>
          )}
          <ChevronDown size={14} className="text-gray-400" />
        </span>
      </button>

      {abierto && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-72 overflow-hidden flex flex-col"
          data-testid={`${testid}-panel`}>
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por nombre o cargo"
                className="w-full border border-gray-200 rounded-lg pl-7 pr-2 py-1.5 text-xs"
                data-testid={`${testid}-buscar`} />
            </div>
          </div>
          <label className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
            <input type="checkbox" checked={todosMarcados} onChange={alternarTodos} data-testid={`${testid}-todos`} />
            Seleccionar todos ({lista.length})
          </label>
          <div className="overflow-y-auto">
            {lista.length === 0 ? (
              <p className="px-3 py-4 text-xs text-gray-400 text-center">No hay personal cargado en la nómina</p>
            ) : lista.map(w => (
              <label key={w.id}
                className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 cursor-pointer"
                data-testid={`${testid}-op-${w.id}`}>
                <input type="checkbox" checked={seleccionados.includes(w.id)} onChange={() => alternar(w.id)} />
                <span className="flex-1 truncate text-gray-900">{w.name}</span>
                {w.role && <span className="text-[10px] text-gray-400 truncate max-w-[110px]">{w.role}</span>}
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${ESTADO_CHIP[w.status] || ESTADO_CHIP.inactivo}`}>
                  {w.status || "sin estado"}
                </span>
              </label>
            ))}
          </div>
          <button type="button" onClick={() => setAbierto(false)}
            className="border-t border-gray-100 py-1.5 text-xs font-semibold text-[#2E6DA4] hover:bg-gray-50 flex items-center justify-center gap-1"
            data-testid={`${testid}-listo`}>
            <Check size={12} /> Listo
          </button>
        </div>
      )}
    </div>
  );
}
